/* eslint-disable no-unused-vars */
import qz, { PrintData } from 'qz-tray';
import React, { FC, memo, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router';
import { cloneDeep } from 'lodash';
import { unwrapResult } from '@reduxjs/toolkit';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Select
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import LocalAtmIcon from '@material-ui/icons/LocalAtm';
import PrintIcon from '@material-ui/icons/Print';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';

import { LinkComponent } from 'src/components';
import { colors } from 'src/constants';

import {
  formatingDate,
  formatCurrency,
  formatDate,
  getBranchColor,
  getDurationToNext,
  getPaymentTypeColor,
  getTotalTimelineDuration
} from 'src/utils';

import TapTapRunningDuration from './TapTapRunningDuration';

import {
  TapTapCustomerData,
  TapTapCustomerPayment,
  TaptapTimelineStatuses
} from 'src/redux/slices/taptap-customer/types';

import {
  taptapOptionsStatusesV2,
  taptapStatuses
} from 'src/redux/slices/taptap-customer/constants/taptap-flow-process';

import {
  TAPTAP_CREATE_SALES_TRANSFER_TRANSACTION_ACTION,
  TaptapNextAction
} from 'src/redux/slices/taptap-customer/flow/taptap-flow-engine';
import { ReservedTag } from './ReservedTag';
import { usePermissions, useSnackBar } from 'src/hooks';
import { TicketComment } from './TicketComment';
import { slices, useAppDispatch } from 'src/redux';
import { Transaction } from 'src/types';
import { sortProductsByCategory } from 'src/utils/categories/sortByCategory';
import { SupabaseClientService } from 'src/services';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import { addTicketComment } from 'src/redux/slices/taptap-customer';

const { actions: transactionActions } = slices.transaction;
const { actions: transactionPaymentActions } = slices.transactionPayments;
const FOR_RMA_TAG = 'FOR RMA';

const StyledSelect = withStyles((theme) => ({
  root: {
    color: 'white',
    padding: theme.spacing(1),
    paddingLeft: 20,
    textTransform: 'uppercase',
    minWidth: 230
  }
}))(Select);

interface Props {
  open: boolean;
  selectedCustomer?: TapTapCustomerData;
  timeline: TaptapTimelineStatuses[];

  isReadOnly?: boolean;
  isStatusLoading: boolean;
  isHistoryLoading: boolean;

  classes: any;

  paymentTotal: number;
  nextAction: TaptapNextAction;

  onClose: () => void;
  onChangeStatus: (value: string) => void;
  onHasSystemGeneratedTicketChange?: (value: boolean) => void;
  onCommentFetchingChange?: (value: boolean) => void;
  onProceedNextStatus: (status?: string) => void;
  onEditPayment: (payment: TapTapCustomerPayment) => void;
}

const getStatusBgColor = (status?: string) =>
  taptapOptionsStatusesV2.find((option) => option.value === status)?.bgColor ||
  colors.grey[400];

const pulseKeyframes = `
  @keyframes balancePulse {
    0%   { transform: scale(1); }
    30%  { transform: scale(1.18); }
    60%  { transform: scale(0.95); }
    80%  { transform: scale(1.1); }
    100% { transform: scale(1); }
  }
`;

const BalancePulseStyle = () => <style>{pulseKeyframes}</style>;

const receiptWidth = 32;

const rawHex = (data: string): PrintData => ({
  type: 'raw',
  format: 'command',
  data,
  flavor: 'hex'
});

const rawPlain = (data: string): PrintData => ({
  type: 'raw',
  format: 'command',
  data,
  flavor: 'plain'
});

const divider = () => `${'-'.repeat(receiptWidth)}\n`;

const wrapText = (value: string, width = receiptWidth) => {
  const words = value
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const lines: string[] = [];
  let currentLine = '';

  words.forEach((word) => {
    if (!currentLine) {
      currentLine = word;
      return;
    }

    if (`${currentLine} ${word}`.length <= width) {
      currentLine = `${currentLine} ${word}`;
      return;
    }

    lines.push(currentLine);
    currentLine = word;
  });

  if (currentLine) lines.push(currentLine);
  return lines.length ? lines : ['--'];
};

const getItemSerials = (item: Transaction) => {
  const serials = [
    item.serial_no,
    ...(item.serial_nos || []),
    ...(item.bundle_items || []).map((bundleItem) => bundleItem.serial_no)
  ].filter(Boolean);

  return serials.length ? serials : ['--'];
};

const formatPickListItems = (items: Transaction[]) => {
  if (!items.length) return 'No items found.\n';

  return items
    .map((item, index) => {
      const productName = item.product_name || '--';
      const quantity = item.quantity ? ` x${item.quantity}` : '';
      const productLines = wrapText(`${productName}${quantity}`);
      const serialLines = getItemSerials(item).flatMap((serial) =>
        wrapText(`SN: ${serial}`)
      );

      return [...productLines, ...serialLines].join('\n');
    })
    .join(`\n${divider()}`);
};

const formatPickListItemsV2 = (items: any[]) => {
  if (!items.length) return 'No items found.\n';

  return items
    .map((item, index) => {
      const productName = item.product_name || '--';
      const quantity = item.quantity ? ` x${item.quantity}` : '';
      const serials = getItemSerials(item);
      const sku = item.sku || '--';
      const productLines = wrapText(`${index + 1}. ${productName}${quantity}`);
      const skuLines = wrapText(`SKU: ${sku}`);
      const serialLines = serials
        .filter(Boolean)
        .flatMap((serial: any) => wrapText(`SN: ${serial}`));

      return [...productLines, ...skuLines, ...serialLines].join('\n');
    })
    .join(`\n${divider()}`);
};

const Component: FC<Props> = ({
  open,
  selectedCustomer,
  timeline,
  isReadOnly = false,
  isStatusLoading,
  isHistoryLoading,
  classes,
  paymentTotal,
  nextAction,
  onClose,
  onChangeStatus,
  onHasSystemGeneratedTicketChange,
  onCommentFetchingChange,
  onProceedNextStatus,
  onEditPayment
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    canViewAllTaptapDashboard,
    canViewTaptapSalesDashboard,
    canViewTaptapCashierDashboard
  } = usePermissions();
  const customerPayments = selectedCustomer?.customers_payments;
  const snackBar = useSnackBar();
  const [isTaggingReserved, setIsTaggingReserved] = useState(false);

  //RMA STATES
  const [isRmaSectionOpen, setIsRmaSectionOpen] = useState(false);
  const [isFetchingRmaItems, setIsFetchingRmaItems] = useState(false);
  const [isSubmittingRma, setIsSubmittingRma] = useState(false);
  const [rmaItems, setRmaItems] = useState<Transaction[]>([]);
  const [selectedRmaItemIds, setSelectedRmaItemIds] = useState<number[]>([]);
  const rmaFetchTokenRef = useRef(0);
  const selectedCustomerId = selectedCustomer?.id;

  const isRmaChangedStatus =
    selectedCustomer?.status === taptapStatuses.RMA_CHANGED_ITEM;
  const isProceedButtonDisabled = isRmaChangedStatus
    ? Boolean(isStatusLoading)
    : nextAction.disabled;
  const proceedButtonDisabledReason = isRmaChangedStatus
    ? ''
    : nextAction.disabledReason;
  const isCreateSalesTransferTransactionAction =
    nextAction.status === TAPTAP_CREATE_SALES_TRANSFER_TRANSACTION_ACTION;
  const canCreateSalesTransferTransaction =
    canViewAllTaptapDashboard || canViewTaptapSalesDashboard;
  const shouldShowProceedButton =
    !isCreateSalesTransferTransactionAction ||
    canCreateSalesTransferTransaction;

  const clearRmaState = () => {
    rmaFetchTokenRef.current += 1;
    setIsRmaSectionOpen(false);
    setIsFetchingRmaItems(false);
    setIsSubmittingRma(false);
    setRmaItems([]);
    setSelectedRmaItemIds([]);
  };

  useEffect(() => {
    clearRmaState();
  }, [open, selectedCustomerId]);

  const handleFetchRmaItems = async () => {
    const transactionNo = selectedCustomer?.internal_transaction_no;

    if (!transactionNo) {
      alert('No transaction number found.');
      return;
    }

    setIsRmaSectionOpen(true);
    setIsFetchingRmaItems(true);
    const fetchToken = rmaFetchTokenRef.current + 1;
    rmaFetchTokenRef.current = fetchToken;

    try {
      const transactionInfoApiResponse: any = unwrapResult(
        await dispatch(
          transactionActions.getTransactionViaTransactionNo(transactionNo)
        )
      );
      const transactionInfo = transactionInfoApiResponse?.originalData;

      if (!transactionInfo?.success) {
        throw new Error('Failed to fetch transaction data.');
      }

      if (rmaFetchTokenRef.current === fetchToken) {
        setRmaItems(transactionInfo?.items || []);
        setSelectedRmaItemIds([]);
      }
    } catch (error) {
      if (rmaFetchTokenRef.current === fetchToken) {
        console.error(error);
        alert('Failed to fetch RMA items.');
      }
    } finally {
      if (rmaFetchTokenRef.current === fetchToken) {
        setIsFetchingRmaItems(false);
      }
    }
  };

  const handleToggleRmaItem = (id?: number) => {
    if (!id) return;

    setSelectedRmaItemIds((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    );
  };

  const handleSubmitRmaReport = async () => {
    if (!selectedCustomer?.id || selectedRmaItemIds.length === 0) return;

    const selectedItems = rmaItems.filter(
      (item) => item.id && selectedRmaItemIds.includes(item.id)
    );
    const comment = selectedItems
      .map((item) => `${item.product_name || '--'} - ${item.serial_no || '--'}`)
      .join('\n');

    setIsSubmittingRma(true);

    try {
      const { error: commentError } = await addTicketComment({
        customer_id: selectedCustomer.id,
        user_id: undefined,
        full_name: 'System',
        comment,
        tag: FOR_RMA_TAG
      });

      if (commentError) {
        throw commentError;
      }

      const { error: customerError } = await SupabaseClientService.from(
        'customers'
      )
        .update({
          tag: FOR_RMA_TAG
        })
        .eq('id', selectedCustomer.id);

      if (customerError) {
        throw customerError;
      }

      onChangeStatus(taptapStatuses.RMA_CHANGED_ITEM);
      setIsRmaSectionOpen(false);
      setSelectedRmaItemIds([]);
      onClose();
    } catch (error) {
      console.error(error);
      snackBar.show({
        severity: 'error',
        message: 'Failed to submit RMA report.'
      });
    } finally {
      setIsSubmittingRma(false);
    }
  };

  const handleProceedNextStatus = async () => {
    if (
      nextAction.status === TAPTAP_CREATE_SALES_TRANSFER_TRANSACTION_ACTION &&
      selectedCustomer?.id
    ) {
      if (!canViewAllTaptapDashboard && !canViewTaptapSalesDashboard) {
        return;
      }

      navigate(
        `/app/quotation-v2?sales_transfer_ticket_id=${selectedCustomer.id}`
      );
      onClose();
      return;
    }

    if (isRmaChangedStatus && selectedCustomer?.id) {
      await SupabaseClientService.from('ticket_comments')
        .update({ deleted_at: new Date().toISOString() })
        .eq('customer_id', selectedCustomer.id)
        .is('deleted_at', null)
        .eq('tag', FOR_RMA_TAG);

      await SupabaseClientService.from('customers')
        .update({ tag: null })
        .eq('id', selectedCustomer.id);
    }

    onProceedNextStatus(nextAction.status || undefined);
  };

  const allScreenshots = useMemo(() => {
    return (customerPayments || []).flatMap((payment) =>
      (payment?.screenshots || []).map((s) => ({
        img_url: s.img_url,
        payment_type: payment.payment_type,
        created_at: s.created_at || payment.created_at
      }))
    );
  }, [customerPayments]);

  const handleTagReserved = async () => {
    if (!selectedCustomer?.id) return;
    setIsTaggingReserved(true);

    const isCurrentlyTagged = !!selectedCustomer?.tag;

    const { error } = await SupabaseClientService.from('customers')
      .update({ tag: isCurrentlyTagged ? null : 'FOR RESERVED' })
      .eq('id', selectedCustomer.id);

    if (error) {
      alert('Failed to update tag: ' + error.message);
      setIsTaggingReserved(false);
      return;
    }

    setIsTaggingReserved(false);
  };

  const totalTimelineDuration = useMemo(() => {
    const timelineWithDates = (timeline || [])
      .filter((log) => log.created_at)
      .map((log) => ({ created_at: log.created_at || '' }));

    return getTotalTimelineDuration(timelineWithDates);
  }, [timeline]);
  const [isPulsingBalance, setIsPulsingBalance] = useState(false);

  const triggerBalancePulse = () => {
    setIsPulsingBalance(false);
    // force reflow so animation restarts even if clicked multiple times
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setIsPulsingBalance(true));
    });
    setTimeout(() => setIsPulsingBalance(false), 600);
  };

  const hasBalance = Boolean(
    selectedCustomer?.balance && selectedCustomer.balance > 0
  );
  const isBlockedByBalance =
    nextAction.disabledReason === 'Please settle the balance first.';

  const handlePrintPickList = async () => {
    try {
      const transactionNo = selectedCustomer?.internal_transaction_no;

      if (!transactionNo) {
        throw new Error('No transaction number found.');
      }

      const transactionInfoApiResponse: any = unwrapResult(
        await dispatch(
          transactionActions.getTransactionViaTransactionNo(transactionNo)
        )
      );
      const transactionPaymentsApiResponse: any = unwrapResult(
        await dispatch(
          transactionPaymentActions.getTransactionPaymentsThunk(transactionNo)
        )
      );

      const transactionInfo = transactionInfoApiResponse?.originalData;
      const transactionPaymentsInfo =
        transactionPaymentsApiResponse?.originalData;
      const totalBalance = transactionPaymentsInfo?.total_balance;
      console.log('Fetched transaction info for printing:', transactionInfo);
      console.log(
        'Fetched transaction payments for printing:',
        transactionPaymentsInfo
      );
      console.log(
        'Fetched transaction payments for printing:',
        transactionPaymentsInfo
      );

      if (!transactionInfo?.success) {
        throw new Error('Failed to fetch transaction data.');
      }

      const sortedItems = sortProductsByCategory(
        cloneDeep(
          (transactionInfo?.items || []).filter((item: any) => !item.isDeleted)
        )
      );
      const customerName = `${selectedCustomer?.first_name ||
        ''} ${selectedCustomer?.last_name || ''}`.trim();

      const pickListPayload: PrintData[] = [
        rawPlain('\n'),
        rawHex('1B6101'),
        rawPlain('PICK LIST\n'),
        rawPlain('PC Worth\n'),
        rawPlain('\n'),
        rawHex('1B6100'),
        rawPlain(`Transaction: ${transactionNo}\n`),
        rawPlain(`Customer: ${customerName || '--'}\n`),
        rawPlain(`Status: ${selectedCustomer?.status || '--'}\n`),
        rawPlain(
          `Total Balance: ${
            totalBalance != null ? `PHP ${totalBalance}` : '--'
          }\n`
        ),
        rawPlain(`Printed: ${formatDate(new Date(), 'MMM d, yyyy h:mm a')}\n`),
        rawPlain(divider()),
        rawPlain('ITEMS / SERIALS\n'),
        rawPlain(divider()),
        rawPlain(`${formatPickListItems(sortedItems)}\n`),
        rawPlain(divider()),
        rawPlain(`Total Items: ${sortedItems.length}\n`),
        rawPlain(
          `${wrapText('THIS DOCUMENT IS NOT VALID FOR CLAIM OF INPUT TAX').join(
            '\n'
          )}\n`
        ),
        rawPlain('\n\n\n')
      ];

      await qz.websocket.connect();
      const printers = await qz.printers.find();
      let targetPrinter;
      console.log('Available printerss:', printers);

      const isSupportedPrinter = (name: string) => {
        const lower = name.toLowerCase();
        return lower.includes('yichip') || lower.includes('xp-58');
      };

      if (Array.isArray(printers)) {
        targetPrinter = printers?.find((p) => isSupportedPrinter(p));
      } else if (typeof printers === 'string') {
        targetPrinter = isSupportedPrinter(printers) ? printers : undefined;
      }

      if (!targetPrinter) {
        throw new Error('XP-58H / Yichip printer not found');
      }

      const config = qz.configs.create(targetPrinter);

      console.log('Using printer:', targetPrinter);
      console.log('config:', config);
      console.log('pickListPayload:', pickListPayload);

      await qz.print(config, pickListPayload);
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      console.error(e);
      alert('Pick List Print Error: ' + message);
    } finally {
      if (qz.websocket.isActive()) {
        qz.websocket.disconnect();
      }
    }
  };

  const handlePrintPickListV2 = async () => {
    try {
      const transactionNo = selectedCustomer?.internal_transaction_no;

      if (!transactionNo) {
        throw new Error('No transaction number found.');
      }

      const transactionInfoApiResponse: any = unwrapResult(
        await dispatch(
          transactionActions.getTransactionViaTransactionNo(transactionNo)
        )
      );
      const transactionPaymentsApiResponse: any = unwrapResult(
        await dispatch(
          transactionPaymentActions.getTransactionPaymentsThunk(transactionNo)
        )
      );

      const transactionInfo = transactionInfoApiResponse?.originalData;
      const transactionPaymentsInfo =
        transactionPaymentsApiResponse?.originalData;
      const totalBalance = transactionPaymentsInfo?.total_balance;

      if (!transactionInfo?.success) {
        throw new Error('Failed to fetch transaction data.');
      }

      const sortedItems = sortProductsByCategory(
        cloneDeep(
          (transactionInfo?.items || []).filter(
            (item: any) => !item.isDeleted
          )
        )
      );
      const customerName = `${selectedCustomer?.first_name || ''} ${
        selectedCustomer?.last_name || ''
      }`.trim();

      // V2 carries SKU per item + a sign-off block (assignatories).
      // SKU now arrives on each item from getTransactionsViaTransactionNo,
      // so no extra API lookups are needed.
      const assignatoryBlock = [
        '',
        divider(),
        'SIGN BELOW',
        divider(),
        'Warehouse: ______________________',
        '',
        'Cashier:   ______________________',
        '',
        'Tech:      ______________________',
        '',
        'Released By: ____________________',
        '',
        'Customer:  ______________________',
        ''
      ].join('\n');

      const pickListV2Payload: PrintData[] = [
        rawPlain('\n'),
        rawHex('1B6101'),
        rawPlain('PICK LIST V2\n'),
        rawPlain('PC Worth\n'),
        rawPlain('\n'),
        rawHex('1B6100'),
        rawPlain(`Transaction: ${transactionNo}\n`),
        rawPlain(`Customer: ${customerName || '--'}\n`),
        rawPlain(`Status: ${selectedCustomer?.status || '--'}\n`),
        rawPlain(
          `Total Balance: ${
            totalBalance != null ? `PHP ${totalBalance}` : '--'
          }\n`
        ),
        rawPlain(
          `Printed: ${formatDate(new Date(), 'MMM d, yyyy h:mm a')}\n`
        ),
        rawPlain(divider()),
        rawPlain('ITEMS / SKU / SERIALS\n'),
        rawPlain(divider()),
        rawPlain(`${formatPickListItemsV2(sortedItems)}\n`),
        rawPlain(divider()),
        rawPlain(`Total Items: ${sortedItems.length}\n`),
        rawPlain(assignatoryBlock),
        rawPlain(
          `${wrapText('THIS DOCUMENT IS NOT VALID FOR CLAIM OF INPUT TAX').join(
            '\n'
          )}\n`
        ),
        rawPlain('\n\n\n')
      ];

      await qz.websocket.connect();
      const printers = await qz.printers.find();
      let targetPrinter;
      console.log('Available printerss:', printers);

      const isSupportedPrinter = (name: string) => {
        const lower = name.toLowerCase();
        return lower.includes('yichip') || lower.includes('xp-58');
      };

      if (Array.isArray(printers)) {
        targetPrinter = printers?.find((p) => isSupportedPrinter(p));
      } else if (typeof printers === 'string') {
        targetPrinter = isSupportedPrinter(printers) ? printers : undefined;
      }

      if (!targetPrinter) {
        throw new Error('XP-58H / Yichip printer not found');
      }

      const config = qz.configs.create(targetPrinter);

      console.log('Using printer:', targetPrinter);
      console.log('config:', config);
      console.log('pickListV2Payload:', pickListV2Payload);

      await qz.print(config, pickListV2Payload);
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      console.error(e);
      alert('Pick List V2 Print Error: ' + message);
    } finally {
      if (qz.websocket.isActive()) {
        qz.websocket.disconnect();
      }
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle style={{ padding: 20, marginLeft: 10 }}>
        {selectedCustomer?.id ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gridGap={12}
          >
            <LinkComponent
              openInNewTab
              href={`/app/customer-taptap/${selectedCustomer.id}`}
              title={
                <Typography variant="h4">{`${selectedCustomer?.first_name ||
                  ''} ${selectedCustomer?.last_name || ''}`}</Typography>
              }
            />

            {!isReadOnly ? (
              <>
                {selectedCustomer?.status === 'COMPLETED' ||
                !shouldShowProceedButton ? null : (
                  <Tooltip title={proceedButtonDisabledReason}>
                    {/* span catches clicks even when Button is disabled */}
                    <span
                      style={{
                        cursor: isProceedButtonDisabled ? 'not-allowed' : 'auto'
                      }}
                      onClick={() => {
                        if (isBlockedByBalance) triggerBalancePulse();
                      }}
                    >
                      <Button
                        color="primary"
                        variant="contained"
                        disabled={isProceedButtonDisabled}
                        onClick={() => {
                          if (isBlockedByBalance) {
                            triggerBalancePulse();
                            return;
                          }
                          handleProceedNextStatus();
                        }}
                      >
                        {isRmaChangedStatus ? 'Resolve RMA' : nextAction.label}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </>
            ) : null}
          </Box>
        ) : null}
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {(isStatusLoading || isHistoryLoading) && <LinearProgress />}
        <Grid container spacing={1}>
          <Grid
            item
            xs={12}
            md={8}
            style={{ maxHeight: 700, overflow: 'auto' }}
          >
            {selectedCustomer ? (
              <>
                <Box className={classes.previewCard}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={12}>
                      <Box
                        sx={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: { sm: 'flex-start', md: 'flex-end' },
                          flexDirection: { sm: 'column', md: 'row' },
                          mb: 1
                        }}
                      >
                        {selectedCustomer?.internal_transaction_no ? (
                          <LinkComponent
                            openInNewTab
                            href={`/app/transaction/${selectedCustomer.internal_transaction_no}`}
                            title={
                              <Typography variant="h4">
                                {selectedCustomer.internal_transaction_no}
                              </Typography>
                            }
                          />
                        ) : (
                          'No Transaction'
                        )}

                        <Box
                          display="flex"
                          alignItems="flex-end"
                          gridGap={12}
                          flexWrap="wrap"
                          justifyContent="flex-end"
                        >
                          {!isReadOnly && canViewAllTaptapDashboard ? (
                            <Box>
                              <Typography variant="subtitle2">
                                Change status
                              </Typography>

                              <StyledSelect
                                variant="standard"
                                value={selectedCustomer.status || ''}
                                disableUnderline
                                disabled={isStatusLoading}
                                style={{
                                  backgroundColor:
                                    taptapOptionsStatusesV2.find(
                                      (status) =>
                                        status.value === selectedCustomer.status
                                    )?.bgColor || colors.grey[400]
                                }}
                                onChange={(event) => {
                                  if (
                                    event.target.value &&
                                    event.target.value !==
                                      selectedCustomer.status &&
                                    typeof event.target.value === 'string'
                                  ) {
                                    onChangeStatus(event.target.value);
                                  }
                                }}
                              >
                                {taptapOptionsStatusesV2.map((status) => (
                                  <MenuItem
                                    dense
                                    value={status.value}
                                    key={status.value}
                                    style={{
                                      backgroundColor:
                                        status.bgColor || 'inherit',
                                      color: 'white',
                                      fontWeight: 700
                                    }}
                                  >
                                    {status.label}
                                  </MenuItem>
                                ))}
                              </StyledSelect>
                            </Box>
                          ) : null}
                        </Box>
                      </Box>

                      <Typography variant="body2" color="textSecondary">
                        {[
                          selectedCustomer.source,
                          selectedCustomer.courier,
                          selectedCustomer.service_type
                        ]
                          .filter(Boolean)
                          .join(' | ') || '--'}
                      </Typography>
                      <Box className={classes.metaRow}>
                        {selectedCustomer.tag ? (
                          <Chip
                            icon={
                              <LocalOfferIcon
                                style={{ color: colors.common.white }}
                              />
                            }
                            size="small"
                            label={selectedCustomer?.tag || '--'}
                            style={{
                              backgroundColor: '#F9A825',
                              color: 'white',
                              fontWeight: 700
                            }}
                          />
                        ) : null}

                        <Chip
                          size="small"
                          label={selectedCustomer.status || '--'}
                          style={{
                            backgroundColor: getStatusBgColor(
                              selectedCustomer.status
                            ),
                            color: colors.common.white,
                            fontWeight: 700
                          }}
                        />

                        <Chip
                          size="small"
                          label={`Total: ${formatCurrency(
                            selectedCustomer.total_amount
                          )}`}
                        />

                        <Chip
                          size="small"
                          label={`Paid: ${formatCurrency(paymentTotal)}`}
                        />

                        <BalancePulseStyle />
                        <Chip
                          size="small"
                          label={`Balance: ${formatCurrency(
                            selectedCustomer.balance
                          )}`}
                          style={
                            hasBalance
                              ? {
                                  backgroundColor: colors.red[500],
                                  color: colors.common.white,
                                  fontWeight: 700,
                                  animation: isPulsingBalance
                                    ? 'balancePulse 0.6s ease'
                                    : 'none',
                                  transformOrigin: 'center'
                                }
                              : undefined
                          }
                        />
                      </Box>

                      <Typography variant="body2" style={{ marginTop: 8 }}>
                        <span
                          className={classes.branchText}
                          style={{
                            color: getBranchColor(
                              selectedCustomer.branch_released
                            )
                          }}
                        >
                          {selectedCustomer.branch_released || '--'}
                        </span>

                        {' - '}

                        {formatDate(
                          selectedCustomer.created_at,
                          'MMM d, yyyy h:mm a'
                        )}
                      </Typography>
                      {selectedCustomer.status !== taptapStatuses.COMPLETED ? (
                        <TapTapRunningDuration
                          createdAt={selectedCustomer.created_at}
                          hasLog={selectedCustomer.running_status_has_log}
                          onRenderActions={() => (
                            <Box
                              display="flex"
                              alignItems="center"
                              gridGap={12}
                            >
                              {canViewAllTaptapDashboard ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<LocalOfferIcon />}
                                  disabled={isTaggingReserved}
                                  onClick={handleTagReserved}
                                >
                                  {isTaggingReserved
                                    ? 'Updating...'
                                    : selectedCustomer?.tag
                                    ? 'REMOVE RESERVATION TAG'
                                    : 'TAG FOR RESERVATION'}
                                </Button>
                              ) : null}

                              <Button
                                size="small"
                                color="primary"
                                variant="outlined"
                                startIcon={<PrintIcon />}
                                title="Print Pick List"
                                onClick={handlePrintPickList}
                              >
                                Print Pick List
                              </Button>

                              <Button
                                size="small"
                                color='primary'
                                variant="outlined"
                                startIcon={<PrintIcon />}
                                title="Print Pick List V2 (with SKU + sign-off)"
                                onClick={handlePrintPickListV2}
                              >
                                Print Pick List V2
                              </Button>

                              {canViewAllTaptapDashboard ||
                              selectedCustomer.status ===
                                taptapStatuses.TESTING ||
                              selectedCustomer.status ===
                                taptapStatuses.BUILDING ? (
                                <Button
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                  startIcon={<ReportProblemIcon />}
                                  disabled={
                                    isFetchingRmaItems || isSubmittingRma
                                  }
                                  onClick={handleFetchRmaItems}
                                >
                                  Report RMA
                                </Button>
                              ) : null}
                            </Box>
                          )}
                        />
                      ) : null}
                    </Grid>
                  </Grid>
                </Box>

                {isRmaSectionOpen ? (
                  <Box className={classes.previewCard}>
                    <Box className={classes.sectionHeader}>
                      <Typography variant="h5">RMA Change Item</Typography>
                      <Button
                        color="primary"
                        variant="contained"
                        disabled={
                          isFetchingRmaItems ||
                          isSubmittingRma ||
                          selectedRmaItemIds.length === 0
                        }
                        onClick={handleSubmitRmaReport}
                      >
                        {isSubmittingRma ? 'Submitting...' : 'Submit For RMA'}
                      </Button>
                    </Box>

                    {isFetchingRmaItems ? (
                      <LinearProgress />
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox" />
                            <TableCell>Item Name</TableCell>
                            <TableCell>Serial No.</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rmaItems.length ? (
                            rmaItems.map((item) => (
                              <TableRow
                                hover
                                key={item.id}
                                onClick={() => handleToggleRmaItem(item.id)}
                              >
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    color="primary"
                                    checked={
                                      !!item.id &&
                                      selectedRmaItemIds.includes(item.id)
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  {item.product_name || '--'}
                                </TableCell>
                                <TableCell>{item.serial_no || '--'}</TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={3}>No items found.</TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    )}
                  </Box>
                ) : null}

                {/* PAYMENTS */}
                <Box className={classes.previewCard}>
                  <Box className={classes.sectionHeader}>
                    <Typography variant="h5">Payments</Typography>

                    {(!isReadOnly && canViewTaptapCashierDashboard) ||
                    canViewAllTaptapDashboard ? (
                      <LinkComponent
                        openInNewTab
                        href={`/app/transaction/${selectedCustomer.internal_transaction_no}`}
                        title={
                          <Button
                            color="primary"
                            variant="contained"
                            startIcon={<LocalAtmIcon />}
                            disabled={!selectedCustomer.internal_transaction_no}
                          >
                            Add payment
                          </Button>
                        }
                      />
                    ) : null}
                  </Box>

                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>AR / SI No.</TableCell>
                        <TableCell>Type / MOP</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Date Posted</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {selectedCustomer.customers_payments?.length ? (
                        selectedCustomer.customers_payments.map((payment) =>
                          !payment?.deleted_by ? (
                            <TableRow hover key={payment.id}>
                              <TableCell>
                                {payment.acknowledgement_receipt_no || '--'}
                              </TableCell>

                              <TableCell>
                                <Typography
                                  variant="subtitle2"
                                  style={{
                                    color: getPaymentTypeColor(
                                      payment.payment_type
                                    )
                                  }}
                                >
                                  {payment.payment_type || '--'}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                {formatCurrency(payment.amount)}
                              </TableCell>

                              <TableCell>
                                {formatDate(
                                  payment.created_at,
                                  'MMM d, yyyy h:mm a'
                                )}
                              </TableCell>

                              <TableCell align="right">
                                {(!isReadOnly &&
                                  canViewTaptapCashierDashboard) ||
                                canViewAllTaptapDashboard ? (
                                  <IconButton
                                    size="small"
                                    onClick={() => onEditPayment(payment)}
                                  >
                                    <EditOutlinedIcon fontSize="small" />
                                  </IconButton>
                                ) : null}
                              </TableCell>
                            </TableRow>
                          ) : null
                        )
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5}>No payments yet.</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>

                  {allScreenshots?.length > 0 ? (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6">Proof of payment(s)</Typography>
                      {allScreenshots?.map((screenshot, idx) => (
                        <Box
                          key={idx}
                          display="flex"
                          alignItems="center"
                          p={1}
                          borderRadius={6}
                          style={{
                            border: '1px solid #e0e0e0',
                            backgroundColor: '#ffffff',
                            gap: 12
                          }}
                        >
                          <img
                            src={screenshot.img_url}
                            alt={`Payment screenshot ${idx + 1}`}
                            style={{
                              width: 56,
                              height: 56,
                              objectFit: 'cover',
                              borderRadius: 6,
                              flexShrink: 0,
                              border: '1px solid #e0e0e0'
                            }}
                          />
                          <Box flex={1}>
                            <Typography
                              variant="body2"
                              display="block"
                              style={{ fontWeight: 600, fontSize: 14 }}
                            >
                              Screenshot{' '}
                              {allScreenshots.length > 1 ? `#${idx + 1}` : ''}
                            </Typography>
                            <Typography
                              variant="caption"
                              style={{ color: '#9e9e9e', fontSize: 12 }}
                            >
                              {screenshot.payment_type}
                              {screenshot.created_at
                                ? ` · ${formatDate(
                                    screenshot.created_at,
                                    'MMM d, yyyy'
                                  )}`
                                : ''}
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            onClick={() =>
                              window.open(screenshot.img_url, '_blank')
                            }
                            style={{ flexShrink: 0, fontSize: 12 }}
                          >
                            View
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  ) : null}
                </Box>

                {/* TIMELINE */}
                {canViewAllTaptapDashboard ? (
                  <Box className={classes.previewCard}>
                    <Box className={classes.sectionHeader}>
                      <Typography variant="h5">
                        Ticket Timeline:{' '}
                        {selectedCustomer.internal_transaction_no || '--'}
                      </Typography>

                      <Typography variant="h6">
                        Total Processing Time: {totalTimelineDuration}
                      </Typography>
                    </Box>

                    <Divider />

                    <Box
                      className={classes.timelineGrid}
                      style={{ fontWeight: 600 }}
                    >
                      <Typography variant="body2">Status</Typography>
                      <Typography variant="body2">Updated By</Typography>
                      <Typography variant="body2">
                        Change Status Date
                      </Typography>
                      <Typography variant="body2">Duration</Typography>
                    </Box>

                    {timeline.length > 0 ? (
                      timeline.map((log, index, arr) => {
                        const next = arr[index - 1];

                        const duration = next?.created_at
                          ? getDurationToNext(next.created_at, log.created_at)
                          : '-';

                        return (
                          <Box
                            className={classes.timelineGrid}
                            key={`${log.status}-${log.created_at}-${index}`}
                          >
                            <Typography variant="h6" style={{ fontSize: 14 }}>
                              {log.status}
                            </Typography>

                            <Typography variant="body2">
                              {log.updated_by || '-'}
                            </Typography>

                            <Typography variant="body2">
                              {log.created_at
                                ? formatingDate(log.created_at)
                                : '-'}
                            </Typography>

                            <Typography variant="body2">{duration}</Typography>
                          </Box>
                        );
                      })
                    ) : (
                      <Typography
                        variant="body2"
                        style={{ opacity: 0.6, marginTop: 12 }}
                      >
                        No timeline registered
                      </Typography>
                    )}
                  </Box>
                ) : null}
              </>
            ) : null}
          </Grid>

          <Grid item xs={12} md={4}>
            <TicketComment
              customerId={selectedCustomer?.id}
              status={selectedCustomer?.status}
              isReadOnly={isReadOnly}
              hasSystemGeneratedTicket={onHasSystemGeneratedTicketChange}
              onFetchingChange={onCommentFetchingChange}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        style={{
          padding: 20,
          display: 'flex',
          justifyContent: 'space-between'
        }}
      >
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const TicketDialog = memo(Component);
